import React from 'react';

import Wahanda from 'common/wahanda';
import { DailySalesReportAnalytics } from 'common/analytics';
import { buildSalesReportDataCells } from '../common/buildSalesReportDataCells';
import { salesReportHeaderRows, SalesReportMetrics } from '../common/salesReportTableLayout';
import SalesReportHashLink from '../SalesReport/hashLink';
import type { SharedProps } from 'reconnect-types/moduleFederation';

type DailySalesRow = SalesReportMetrics & {
  date: any;
};

type DailySalesReportData = {
  rows: DailySalesRow[];
  total: SalesReportMetrics;
};

export type DailySalesReportTableProps = {
  venueId: string;
  selectedEmployeeId: number;
  selectedChannel: string;
  reportData: DailySalesReportData;
};

const dailySalesDataCellTestIds = {
  servicesQty: 'daily-sales-services-qty',
  servicesGross: 'daily-sales-services-gross',
  productsQty: 'daily-sales-products-qty',
  productsGross: 'daily-sales-products-gross',
  voucherQuantity: 'daily-sales-vouchers-qty',
  voucherGross: 'daily-sales-vouchers-gross',
  prepaid: 'daily-sales-prepaid',
  total: 'daily-sales-grand-total',
};

export const getDailySalesReportTableProps = ({
  venueId,
  selectedEmployeeId,
  selectedChannel,
  reportData,
}: DailySalesReportTableProps): SharedProps['ReportTable'] => {
  const headings = Wahanda.lang.reports.sales.dailySalesReport.headings;

  const headerRows = salesReportHeaderRows({
    firstColumn: headings.date,
    services: headings.services,
    products: headings.products,
    vouchers: headings.vouchers,
    // `prepaid` is only defined under productSalesReport in the locale files.
    prepaid: Wahanda.lang.reports.sales.productSalesReport.headings.prepaid,
    total: headings.totalSalesIncludingVat,
    quantity: headings.quantity,
    sales: headings.salesIncludingVat,
  });

  const tableRows = reportData.rows.map((row) => {
    const navLinkProps = {
      venueId,
      fromDate: row.date,
      toDate: row.date,
      employeeFilter: selectedEmployeeId,
      selectedChannel,
    };

    return {
      id: row.date.formatDateDefault(),
      dataTestId: 'daily-sales-row',
      cells: [
        {
          content: (
            <SalesReportHashLink
              {...navLinkProps}
              onClick={DailySalesReportAnalytics.trackDateLinkClick}
            >
              {row.date.formatDateDefault()}
            </SalesReportHashLink>
          ),
          dataTestId: 'daily-sales-date',
        },
        ...buildSalesReportDataCells(row, dailySalesDataCellTestIds, {
          ...navLinkProps,
          onServiceLinkClick: DailySalesReportAnalytics.trackLinkToServicesSalesClick,
          onProductLinkClick: DailySalesReportAnalytics.trackLinkToProductsSalesClick,
        }),
      ],
    };
  });

  const footerRow = {
    cells: [
      { content: headings.grandTotal },
      ...buildSalesReportDataCells(reportData.total, dailySalesDataCellTestIds).map((cell) => ({
        ...cell,
        variant: 'grandTotal' as const,
      })),
    ],
  };

  return {
    headerRows,
    rows: tableRows,
    footerRow,
    footerDataTestId: 'daily-sales-totals',
    testID: 'daily-sales-report-table',
  };
};
